import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import SEOMeta from '../../components/SEOMeta';
import ThemeToggle from '../../components/ThemeToggle';
import { useDarkMode } from '../../hooks/useDarkMode';

const sections = [
  { id: 'sec-1', label: '1. Aceitação dos Termos' },
  { id: 'sec-2', label: '2. Descrição do Serviço' },
  { id: 'sec-3', label: '3. Elegibilidade' },
  { id: 'sec-4', label: '4. Cadastro e Responsabilidade' },
  { id: 'sec-5', label: '5. Pedidos e Personalização' },
  { id: 'sec-6', label: '6. Preços e Pagamento' },
  { id: 'sec-7', label: '7. Entrega e Prazo' },
  { id: 'sec-8', label: '8. Cancelamento e Reembolso' },
  { id: 'sec-9', label: '9. Programa de Afiliados' },
  { id: 'sec-10', label: '10. Propriedade Intelectual' },
  { id: 'sec-11', label: '11. Limitação de Responsabilidade' },
  { id: 'sec-12', label: '12. Disposições Gerais' },
];

export default function TermsPage() {
  const isDark = useDarkMode();
  const [activeSection, setActiveSection] = useState('sec-1');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <SEOMeta
        title="Termos de Serviço | Inspira Músicas"
        description="Leia os termos de uso do serviço Inspira Músicas, incluindo condições de pagamento, entrega, reembolso e programa de afiliados."
        path="/termos-de-servico"
      />
    <div style={{ background: 'var(--cream)', minHeight: '100vh' }}>
      {/* Header */}
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'shadow-sm' : ''}`}
        style={{
          background: scrolled ? 'var(--warm-white)' : 'transparent',
          borderBottom: scrolled ? '1px solid var(--beige-mid)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
            <img
              src={isDark ? '/logo-dark.png' : '/logo-light.png'}
              alt="Inspira Músicas"
              className="h-9 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="hidden md:flex items-center gap-2 text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap"
            style={{ color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--gold)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--earth)')}
          >
            <i className="ri-arrow-left-line text-base" />
            Voltar ao início
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Link
              to="/#preco"
              className="px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
              style={{ background: 'var(--gold)', color: '#fff', fontFamily: "'DM Sans', sans-serif" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--amber-deep)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--gold)')}
            >
              Pedir Minha Música
            </Link>
          </div>
        </div>
      </header>

      {/* Hero strip */}
      <div className="pt-16" style={{ background: 'var(--beige-mid)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="text-center mb-6">
            <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'var(--amber-deep)', fontFamily: "'DM Sans', sans-serif" }}>
              AUTOMAT TECNOLOGIA DA INFORMACAO LTDA
            </p>
            <p className="text-xs" style={{ color: 'var(--earth-light)', fontFamily: "'DM Sans', sans-serif" }}>
              CNPJ 59.640.197/0001-76 · Rua Pais Leme, 215, Conj. 1713, Sala 01, Andar 16 — Pinheiros, São Paulo/SP — CEP 05424-150
            </p>
          </div>
          <h1
            className="text-3xl md:text-5xl text-center mb-3"
            style={{ color: 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
          >
            Termos de Serviço
          </h1>
          <p className="text-center text-sm italic" style={{ color: 'var(--amber-deep)', fontFamily: "'DM Sans', sans-serif" }}>
            Última atualização: 10 de abril de 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">

          {/* Sidebar TOC */}
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-24 rounded-2xl p-6" style={{ background: 'var(--warm-white)', border: '1px solid var(--beige-mid)' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--amber-deep)', fontFamily: "'DM Sans', sans-serif" }}>
                Sumário
              </p>
              <ul className="flex flex-col gap-1">
                {sections.map((sec) => (
                  <li key={sec.id}>
                    <button
                      onClick={() => scrollToSection(sec.id)}
                      className="w-full text-left text-xs leading-snug py-1.5 px-2 rounded-lg transition-all duration-200 cursor-pointer"
                      style={{
                        color: activeSection === sec.id ? 'var(--gold)' : 'var(--earth-light)',
                        background: activeSection === sec.id ? 'var(--gold-pale)' : 'transparent',
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: activeSection === sec.id ? 600 : 400,
                      }}
                    >
                      {sec.label}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Related pages */}
              <div className="mt-6 pt-5" style={{ borderTop: '1px solid var(--beige-mid)' }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--amber-deep)', fontFamily: "'DM Sans', sans-serif" }}>
                  Páginas legais
                </p>
                <div className="flex flex-col gap-1.5">
                  {[
                    { label: 'Política de Privacidade', href: '/politica-de-privacidade', icon: 'ri-shield-line' },
                    { label: 'Exclusão de Dados', href: '/exclusao-de-dados', icon: 'ri-delete-bin-line' },
                  ].map((page) => (
                    <Link
                      key={page.href}
                      to={page.href}
                      className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-lg transition-all duration-200 cursor-pointer"
                      style={{ color: 'var(--earth-light)', fontFamily: "'DM Sans', sans-serif" }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--gold)';
                        e.currentTarget.style.background = 'var(--gold-pale)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--earth-light)';
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <i className={`${page.icon} text-xs`} />
                      {page.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Main text */}
          <article className="flex-1 min-w-0">

            {/* Section 1 */}
            <section id="sec-1" className="mb-10 scroll-mt-24">
              <SectionTitle number="1" title="Aceitação dos Termos" />
              <P>
                Ao utilizar o serviço Inspira Músicas, seja por meio do site <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>inspiramusicas.com.br</strong> ou de qualquer outro canal disponibilizado pela Automat, você declara que leu, compreendeu e concorda integralmente com estes Termos de Serviço ("Termos").
              </P>
              <P>
                Caso não concorde com qualquer disposição aqui contida, pedimos que não utilize o serviço.
              </P>
              <P>
                Estes Termos constituem um contrato vinculante entre você ("Usuário" ou "Cliente") e a <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>AUTOMAT TECNOLOGIA DA INFORMACAO LTDA</strong> ("Automat"), responsável pela marca Inspira Músicas.
              </P>
            </section>

            <Divider />

            {/* Section 2 */}
            <section id="sec-2" className="mb-10 scroll-mt-24">
              <SectionTitle number="2" title="Descrição do Serviço" />
              <P>
                O Inspira Músicas é um serviço de criação de músicas personalizadas geradas por inteligência artificial. O funcionamento ocorre da seguinte forma:
              </P>
              <div className="flex flex-col gap-3 my-4">
                {[
                  { step: '1', text: 'O usuário acessa o serviço pelo site e preenche um formulário com informações sobre o destinatário da música e suas preferências musicais.' },
                  { step: '2', text: 'O sistema processa as informações e gera uma composição musical exclusiva — letra, melodia e voz — utilizando tecnologia de inteligência artificial.' },
                  { step: '3', text: 'A música finalizada é entregue por e-mail ao endereço fornecido no momento do pedido.' },
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 rounded-xl p-4" style={{ background: 'var(--warm-white)', border: '1px solid var(--beige-mid)' }}>
                    <span
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                      style={{ background: 'var(--gold-pale)', color: 'var(--gold)', fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {item.step}
                    </span>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
              <P>
                O serviço é prestado integralmente de forma digital, sem entrega de produtos físicos.
              </P>
            </section>

            <Divider />

            {/* Section 3 */}
            <section id="sec-3" className="mb-10 scroll-mt-24">
              <SectionTitle number="3" title="Elegibilidade" />
              <P>
                O serviço é destinado exclusivamente a pessoas físicas com <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>18 (dezoito) anos de idade ou mais</strong>. Ao contratar o serviço, você declara ter capacidade civil plena para celebrar este contrato.
              </P>
              <P>
                A Automat reserva-se o direito de recusar ou cancelar pedidos que violem estes Termos, sem prejuízo das demais medidas cabíveis.
              </P>
            </section>

            <Divider />

            {/* Section 4 */}
            <section id="sec-4" className="mb-10 scroll-mt-24">
              <SectionTitle number="4" title="Cadastro e Responsabilidade do Usuário" />
              <P>
                Para utilizar o serviço, o usuário fornece seu e-mail e as informações necessárias para a personalização da música. Você é responsável por:
              </P>
              <BulletList items={[
                'Fornecer informações verdadeiras, precisas e completas;',
                'Não utilizar dados de terceiros sem autorização;',
                'Manter a confidencialidade de qualquer código ou link de acesso ao seu pedido;',
                'Notificar imediatamente a Automat em caso de uso não autorizado do seu contato.',
              ]} />
              <P>
                A Automat não se responsabiliza por danos decorrentes do fornecimento de informações incorretas ou falsas pelo usuário.
              </P>
            </section>

            <Divider />

            {/* Section 5 */}
            <section id="sec-5" className="mb-10 scroll-mt-24">
              <SectionTitle number="5" title="Pedidos e Personalização" />

              <SubTitle>5.1 Processo de pedido</SubTitle>
              <P>
                O pedido é iniciado pelo site, onde o usuário preenche um formulário com as informações necessárias para a composição. Após a confirmação do pagamento, a música é gerada automaticamente pelo sistema de inteligência artificial.
              </P>

              <SubTitle>5.2 Qualidade e variações</SubTitle>
              <P>
                As músicas são geradas por inteligência artificial e podem apresentar variações de estilo, entonação e interpretação em relação às expectativas do usuário. A Automat não garante que o resultado final corresponderá exatamente à visão do cliente, mas compromete-se a entregar uma composição de qualidade, personalizada com base nas informações fornecidas.
              </P>

              <SubTitle>5.3 Informações fornecidas pelo usuário</SubTitle>
              <P>
                Ao fornecer informações sobre terceiros para a personalização da música — como nome, histórias e características pessoais do destinatário — o usuário declara possuir autorização ou legítimo interesse para compartilhar esses dados, assumindo inteira responsabilidade por qualquer consequência decorrente do uso indevido de informações de terceiros.
              </P>

              <SubTitle>5.4 Conteúdo vedado</SubTitle>
              <P>Não é permitido solicitar músicas cujo conteúdo:</P>
              <BulletList items={[
                'Seja ofensivo, discriminatório, racista, misógino ou que incite ódio ou violência;',
                'Viole direitos de terceiros, incluindo direitos de imagem, honra ou privacidade;',
                'Reproduza letras ou melodias protegidas por direitos autorais de terceiros;',
                'Tenha finalidade ilícita ou contrária à ordem pública e aos bons costumes.',
              ]} />
              <P>
                A Automat reserva-se o direito de recusar pedidos que não atendam a esses critérios, sem obrigação de reembolso em caso de violação intencional destes Termos pelo usuário.
              </P>
            </section>

            <Divider />

            {/* Section 6 */}
            <section id="sec-6" className="mb-10 scroll-mt-24">
              <SectionTitle number="6" title="Preços e Pagamento" />

              <SubTitle>6.1 Preço</SubTitle>
              <P>
                O serviço é oferecido em duas opções informadas no momento do pedido:{' '}
                <strong style={{ color: 'var(--gold)', fontWeight: 700 }}>Música por R$ 34,90</strong>{' '}
                e <strong style={{ color: 'var(--gold)', fontWeight: 700 }}>Música + Vídeo com Fotos por R$ 49,90</strong>.
                A Automat reserva-se o direito de alterar os preços a qualquer tempo, sendo que as alterações não afetarão pedidos já confirmados e pagos.
              </P>

              <SubTitle>6.2 Forma de pagamento</SubTitle>
              <P>
                O pagamento é realizado pelas formas disponibilizadas no momento do pedido, com <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>PIX</strong> como opção preferencial, processado pela Asaas Pagamentos S.A. A finalização da produção da música só se inicia após a confirmação do pagamento pelo sistema.
              </P>

              <SubTitle>6.3 Confirmação</SubTitle>
              <P>
                Após a confirmação do pagamento, o usuário receberá uma mensagem de confirmação e a música será entregue pelo WhatsApp informado no pedido. No pacote com vídeo, o prazo começa após o envio das fotos necessárias à produção.
              </P>

              <SubTitle>6.4 Cupons de desconto</SubTitle>
              <P>
                A Automat pode disponibilizar cupons de desconto que reduzem o valor final cobrado do cliente. Os cupons são pessoais, intransferíveis e de uso único por pedido, salvo disposição contrária expressa no próprio cupom. É vedada a revenda, cessão ou qualquer forma de comercialização de cupons de desconto.
              </P>
            </section>

            <Divider />

            {/* Section 7 */}
            <section id="sec-7" className="mb-10 scroll-mt-24">
              <SectionTitle number="7" title="Entrega e Prazo" />

              <SubTitle>7.1 Canal de entrega</SubTitle>
              <P>
                A música gerada é entregue exclusivamente por <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>e-mail</strong>, no endereço informado pelo usuário no momento do pedido. O usuário é responsável por fornecer um e-mail válido e acessível.
              </P>

              <SubTitle>7.2 Prazo de entrega</SubTitle>
              <P>
                O prazo padrão de entrega é de <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>até 30 (trinta) minutos</strong> após a confirmação do pagamento. Em situações excepcionais — como alta demanda, instabilidade de sistemas de terceiros ou casos de força maior — o prazo poderá ser estendido, e o usuário será informado por e-mail.
              </P>

              <SubTitle>7.3 Não recebimento</SubTitle>
              <P>
                Caso o e-mail de entrega não seja recebido no prazo estipulado, o usuário deve verificar a pasta de spam ou lixo eletrônico antes de contatar o suporte. O atendimento pode ser solicitado pelo e-mail <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>contato@inspiramusicas.com.br</strong>.
              </P>
            </section>

            <Divider />

            {/* Section 8 */}
            <section id="sec-8" className="mb-10 scroll-mt-24">
              <SectionTitle number="8" title="Cancelamento e Reembolso" />

              <SubTitle>8.1 Direito de arrependimento</SubTitle>
              <P>
                Em conformidade com o Código de Defesa do Consumidor (Lei nº 8.078/1990), o usuário pode solicitar o cancelamento e o reembolso integral do valor pago em até <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>7 (sete) dias corridos</strong> a partir da data do pagamento, sem necessidade de justificativa.
              </P>

              <SubTitle>8.2 Garantia de satisfação</SubTitle>
              <P>
                Além do direito legal de arrependimento, a Automat oferece garantia de satisfação: caso o usuário não esteja satisfeito com a música entregue, refazemos a composição gratuitamente ou devolvemos integralmente o valor pago, sem perguntas.
              </P>

              <SubTitle>8.3 Procedimento para reembolso</SubTitle>
              <P>
                Para solicitar o reembolso, o usuário deve enviar um e-mail para <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>contato@inspiramusicas.com.br</strong> informando o número do pedido. O reembolso será processado em até 5 (cinco) dias úteis após a aprovação da solicitação, via estorno à chave PIX utilizada no pagamento.
              </P>

              <SubTitle>8.4 Exceções ao reembolso</SubTitle>
              <P>
                Não haverá reembolso em casos de violação intencional dos presentes Termos pelo usuário, incluindo solicitações de conteúdo vedado descritas na cláusula 5.4.
              </P>
            </section>

            <Divider />

            {/* Section 9 — Affiliates */}
            <section id="sec-9" className="mb-10 scroll-mt-24">
              <SectionTitle number="9" title="Programa de Afiliados" />

              <SubTitle>9.1 Descrição do programa</SubTitle>
              <P>
                O Programa de Afiliados da Inspira Músicas permite que pessoas físicas maiores de 18 anos se cadastrem gratuitamente para receber comissões por vendas realizadas com o uso de seu cupom exclusivo. O cadastro é realizado em <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>inspiramusicas.com.br/afiliados</strong>.
              </P>

              <SubTitle>9.2 Natureza da relação</SubTitle>
              <P>
                A participação no Programa de Afiliados <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>não estabelece qualquer vínculo empregatício, societário, de representação comercial ou de prestação de serviços</strong> entre o afiliado e a Automat. O afiliado atua de forma independente, sem subordinação, exclusividade ou obrigação de resultado. Nenhuma remuneração fixa, benefício trabalhista ou encargo previdenciário é devido pela Automat ao afiliado a qualquer título.
              </P>

              <SubTitle>9.3 Cupom do afiliado</SubTitle>
              <P>
                Ao se cadastrar, o afiliado escolhe um prefixo de identificação, e o sistema gera automaticamente um cupom no formato <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>PREFIXO + VALOR DO DESCONTO</strong> (exemplo: "MARIA10"). O cupom é único, pessoal e intransferível. É vedado ao afiliado:
              </P>
              <BulletList items={[
                'Transferir, ceder, vender ou compartilhar seu cupom com outros afiliados;',
                'Utilizar o próprio cupom em compras realizadas por si mesmo ou por interposta pessoa para gerar comissão artificial (auto-compra);',
                'Criar múltiplos cadastros para obter mais de um cupom;',
                'Utilizar o cupom em contextos que associem a marca Inspira Músicas a conteúdo ofensivo, enganoso ou ilegal.',
              ]} />

              <SubTitle>9.4 Comissão e desconto</SubTitle>
              <P>
                Salvo comunicação em contrário pela Automat, as condições padrão do programa são:
              </P>
              <BulletList items={[
                'Desconto concedido ao comprador: R$ 10,00 (dez reais) por pedido;',
                'Comissão devida ao afiliado: R$ 10,00 (dez reais) por pedido confirmado e não reembolsado.',
              ]} />
              <P>
                A Automat reserva-se o direito de alterar os valores de desconto e comissão a qualquer momento, mediante comunicação prévia aos afiliados cadastrados.
              </P>

              <SubTitle>9.5 Prazo para pagamento das comissões</SubTitle>
              <P>
                As comissões são pagas após o <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>período de garantia de 7 (sete) dias corridos</strong> contado a partir da entrega da música ao comprador. Esse prazo existe para proteger a operação contra reembolsos: enquanto o comprador tiver direito ao reembolso, a comissão permanece em estado pendente.
              </P>
              <P>
                O pagamento é realizado automaticamente via PIX, para a chave cadastrada pelo afiliado, processado em ciclos de até 24 horas após o encerramento do período de garantia.
              </P>

              <SubTitle>9.6 Não pagamento em caso de reembolso</SubTitle>
              <P>
                Se o comprador solicitar e obtiver reembolso dentro do período de garantia, <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>a comissão correspondente àquela venda não será paga</strong>, ainda que o pedido tenha sido entregue. A Automat não se responsabiliza por comissões não pagas em decorrência de pedidos reembolsados.
              </P>

              <SubTitle>9.7 Divulgação responsável</SubTitle>
              <P>
                O afiliado concorda em divulgar o serviço da Inspira Músicas de forma ética, transparente e em conformidade com a legislação aplicável, incluindo as normas do CONAR e as diretrizes das plataformas em que realizar a divulgação. É expressamente vedado:
              </P>
              <BulletList items={[
                'Envio de mensagens não solicitadas em massa (spam), por qualquer canal;',
                'Uso de promessas enganosas, exageradas ou que induzam o consumidor a erro;',
                'Divulgação do serviço sem identificar-se como afiliado quando exigido por lei ou pela plataforma;',
                'Associar a marca Inspira Músicas a conteúdo político, religioso controverso, de ódio ou discriminatório.',
              ]} />

              <SubTitle>9.8 Remoção do programa</SubTitle>
              <P>
                A Automat reserva-se o direito de <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>remover qualquer afiliado do programa a qualquer momento</strong>, a seu exclusivo critério, especialmente em casos de:
              </P>
              <BulletList items={[
                'Violação de qualquer disposição destes Termos;',
                'Práticas comerciais que não se adequem à proposta ou aos valores da marca Inspira Músicas;',
                'Geração de disputas, chargebacks ou reembolsos em volume desproporcional;',
                'Suspeita de fraude ou auto-compra;',
                'Conduta pública que prejudique a reputação da Automat ou da marca.',
              ]} />
              <P>
                A remoção implica a desativação imediata do cupom do afiliado. Comissões relativas a vendas confirmadas e cujo período de garantia já tenha expirado serão pagas normalmente. Comissões ainda pendentes (dentro do período de garantia) poderão ser canceladas em casos de fraude comprovada.
              </P>

              <SubTitle>9.9 Dados cadastrais do afiliado</SubTitle>
              <P>
                O afiliado é responsável por manter atualizados seus dados cadastrais, incluindo a chave PIX para recebimento de comissões. A Automat não se responsabiliza por comissões não pagas em virtude de chave PIX incorreta, desativada ou não pertencente ao afiliado.
              </P>
            </section>

            <Divider />

            {/* Section 10 */}
            <section id="sec-10" className="mb-10 scroll-mt-24">
              <SectionTitle number="10" title="Propriedade Intelectual" />

              <SubTitle>10.1 Direitos sobre a música gerada</SubTitle>
              <P>
                Ao concluir o pagamento, a Automat concede ao usuário uma <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>licença pessoal, não exclusiva e intransferível</strong> para uso da música gerada para fins pessoais e não comerciais.
              </P>

              <SubTitle>10.2 Restrições de uso</SubTitle>
              <P>
                É vedado ao usuário, sem autorização expressa e prévia da Automat:
              </P>
              <BulletList items={[
                'Utilizar a música para fins comerciais, incluindo publicidade, streaming monetizado ou sincronização em produções audiovisuais;',
                'Distribuir, licenciar ou revender a música a terceiros;',
                'Reivindicar autoria ou direitos sobre a composição gerada.',
              ]} />

              <SubTitle>10.3 Uso comercial</SubTitle>
              <P>
                O uso comercial da música gerada requer negociação e autorização prévia por escrito da Automat. Interessados devem entrar em contato pelo e-mail <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>contato@inspiramusicas.com.br</strong>.
              </P>
            </section>

            <Divider />

            {/* Section 11 */}
            <section id="sec-11" className="mb-10 scroll-mt-24">
              <SectionTitle number="11" title="Limitação de Responsabilidade" />
              <P>
                A Automat não se responsabiliza por:
              </P>
              <BulletList items={[
                'Indisponibilidade temporária do serviço por motivos técnicos, manutenção ou caso fortuito;',
                'Falhas em sistemas de terceiros que integrem o serviço, incluindo processadores de pagamento e plataformas de geração de música;',
                'Danos indiretos, incidentais ou consequentes decorrentes do uso ou impossibilidade de uso do serviço;',
                'Não recebimento do e-mail de entrega por bloqueio de provedores ou endereço inválido fornecido pelo usuário.',
              ]} />
              <P>
                Em qualquer hipótese, a responsabilidade máxima da Automat perante o usuário fica limitada ao valor pago pelo pedido.
              </P>
            </section>

            <Divider />

            {/* Section 12 */}
            <section id="sec-12" className="mb-10 scroll-mt-24">
              <SectionTitle number="12" title="Disposições Gerais" />

              <SubTitle>12.1 Alterações dos Termos</SubTitle>
              <P>
                A Automat pode alterar estes Termos a qualquer momento. As alterações entram em vigor imediatamente após a publicação no site. O uso continuado do serviço após a publicação das alterações implica aceitação dos novos Termos.
              </P>

              <SubTitle>12.2 Legislação aplicável e foro</SubTitle>
              <P>
                Estes Termos são regidos pelas leis da República Federativa do Brasil. Fica eleito o foro da comarca de São Paulo/SP para dirimir quaisquer controvérsias oriundas destes Termos, com renúncia de qualquer outro, por mais privilegiado que seja.
              </P>

              <SubTitle>12.3 Contato</SubTitle>
              <P>
                Para dúvidas, sugestões ou solicitações relacionadas a estes Termos, entre em contato pelo e-mail{' '}
                <strong style={{ color: 'var(--earth)', fontWeight: 600 }}>contato@inspiramusicas.com.br</strong>.
              </P>
            </section>

            {/* Company info */}
            <div className="mt-10 pt-8 text-center" style={{ borderTop: '1px solid var(--beige-mid)' }}>
              <p className="text-xs" style={{ color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif" }}>
                AUTOMAT TECNOLOGIA DA INFORMACAO LTDA
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif" }}>
                Rua Pais Leme, 215, Conj. 1713, Sala 01, Andar 16, Pinheiros, São Paulo/SP — CEP 05424-150
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif" }}>
                contato@inspiramusicas.com.br
              </p>
            </div>

            {/* Back to top */}
            <div className="mt-8 text-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{ background: 'var(--beige-mid)', color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--beige-footer)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--beige-mid)')}
              >
                <i className="ri-arrow-up-line text-sm" />
                Voltar ao topo
              </button>
            </div>

          </article>
        </div>
      </div>

      <Footer />
    </div>
    </>
  );
}

/* ---------- helpers ---------- */
function SectionTitle({ number, title }: { number: string; title: string }) {
  return (
    <div className="flex items-start gap-3 mb-4">
      <span
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mt-0.5"
        style={{ background: 'var(--gold-pale)', color: 'var(--gold)', fontFamily: "'DM Sans', sans-serif" }}
      >
        {number}
      </span>
      <h2
        className="text-xl md:text-2xl leading-snug"
        style={{ color: 'var(--heading-ink)', fontFamily: "'DM Sans', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
      >
        {title}
      </h2>
    </div>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className="text-base font-semibold mt-5 mb-2"
      style={{ color: 'var(--earth)', fontFamily: "'DM Sans', sans-serif" }}
    >
      {children}
    </h3>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-sm leading-relaxed mb-3"
      style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
    >
      {children}
    </p>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-2 my-3 ml-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--gold)' }} />
          <span
            className="text-sm leading-relaxed"
            style={{ color: 'var(--text-strong)', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Divider() {
  return <div className="h-px my-8" style={{ background: 'var(--beige-mid)' }} />;
}
